import React, { useMemo } from 'react';
import { PublicPlan } from '@wix/ambassador-pricing-plans-v2-plan/types';
import { Plan } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { VIEWER_HOOKS } from '@wix/pricing-plans-common/hooks';
import { useCurrencyFormatter, useEnvironment } from '@wix/yoshi-flow-editor';
import { useDynamicElement, useIsHydrated } from '../../../../../hooks';
import { useLocale } from '../../../../../hooks/useLocale';
import { getFormattedFees } from '../../../../../utils/plan-unified-utils';
import { st, classes } from './SetupFee.st.css';

interface Props {
  plan: PublicPlan | Plan;
  planId: string;
  highlighted: boolean;
  className?: string;
}

export const SetupFee: React.FC<Props> = ({ plan, planId, highlighted, className }) => {
  const { isMobile } = useEnvironment();
  const { heightByPlanId, registerRef } = useDynamicElement('setup-fee');
  const { locale } = useLocale();
  const isHydrated = useIsHydrated();

  const fees = getFormattedFees({
    plan,
    locale,
    createCurrencyFormatter: useCurrencyFormatter,
    hideTrailingFractionZeroes: true,
  })
    .map(({ amount, name }) => `+${amount} ${name}`)
    .join('\n');

  const ref = useMemo(() => registerRef(planId), [planId, registerRef]);
  const dynamicHeight = useMemo(() => ({ height: heightByPlanId.get(planId) }), [heightByPlanId, planId]);

  return (
    <div
      className={st(classes.root, { highlighted, mobile: isMobile }, className)}
      data-hook={VIEWER_HOOKS.PLAN_SETUP_FEE}
      // setting inline styles causes react to re-render component on hydration, so ony set after hydration
      style={!isMobile && isHydrated ? dynamicHeight : {}}
    >
      <div className={classes.content} ref={!isMobile ? ref : undefined}>
        {fees}
      </div>
    </div>
  );
};
